"use client";

import { isDeadSessionError, recoverDeadSession } from "@/lib/session-recovery";
import { SessionProvider, useSession } from "next-auth/react";
import { useEffect } from "react";

type Props = {
  children?: React.ReactNode;
};

/**
 * Global watchdog for a dead session (see `lib/session-recovery.ts`). The
 * `jwt` callback can't sign the user out itself and the edge middleware can't
 * decrypt the cookie, so the client is the only place that can act on
 * `session.error`.
 */
function SessionErrorGuard() {
  const { data: session } = useSession();
  useEffect(() => {
    if (isDeadSessionError(session?.error)) {
      recoverDeadSession(`session.error=${session?.error}`);
    }
  }, [session?.error]);
  return null;
}

export const NextAuthProvider = ({ children }: Props) => {
  return (
    // Poll the session every 5 minutes so a long-lived tab (a) keeps its access
    // token refreshed server-side via /api/auth/session and (b) learns about a
    // failed refresh without waiting for the user to reload the page.
    <SessionProvider refetchOnWindowFocus={false} refetchInterval={5 * 60}>
      <SessionErrorGuard />
      {children}
    </SessionProvider>
  );
};
